import { message } from 'antd';
import Axios from 'axios';

import { getBaseUrl } from '@libs/NwUtils';

// Percorso dell'endpoint dei file del container privato, completo del prefisso API.
const PRIVATE_FILE_URL_PREFIX = `${getBaseUrl()}/documents/getprivatefile/`;

// Ms prima di rilasciare un object URL aperto in un tab o in un download: il tab ha gia' i dati.
const OBJECT_URL_TTL = 60000;

// Unici tipi che il browser puo' rendere. Il Content-Type non si legge dal server: un html/svg
// aperto da un blob: girerebbe nell'origine dell'app e potrebbe leggere il token. Tutto il resto e' scaricato.
const VIEWABLE_MIME_TYPES = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    txt: 'text/plain',
    csv: 'text/csv',
};

// Percorso relativo a baseURL se il documento sta nel container privato, altrimenti null.
// Ci arrivano due forme: Guid + Extension, oppure Standard.Url relativo (proiezione dei package).
const getPrivateFilePath = (doc) => {
    const standardUrl = doc.Standard?.Url;
    if (standardUrl) {
        return standardUrl.startsWith(PRIVATE_FILE_URL_PREFIX) ? standardUrl.slice(getBaseUrl().length) : null;
    }
    if (doc.Guid && doc.Extension) {
        return `/documents/getprivatefile/${encodeURIComponent(`${doc.Guid}.${doc.Extension}`)}`;
    }
    return null;
};

// URL pubblico del cloud (Standard.Url assoluto), che non richiede autenticazione.
const getPublicUrl = (doc) => {
    const standardUrl = doc.Standard?.Url;
    return standardUrl && !standardUrl.startsWith(PRIVATE_FILE_URL_PREFIX) ? standardUrl : null;
};

const getFileExtension = (path) => path.split('.').pop().toLowerCase();

// Nome del file scaricato: il titolo del documento con la sua estensione, o quello sul server se manca il titolo.
const getDownloadName = (doc, path) => {
    const title = (doc.Title || '').trim().replace(/[\\/:*?"<>|]/g, '_');
    if (!title) {
        return decodeURIComponent(path.split('/').pop());
    }
    const extension = getFileExtension(path);
    return title.toLowerCase().endsWith(`.${extension}`) ? title : `${title}.${extension}`;
};

// Scarica il file con GET autenticato (Authorization messo dall'interceptor di Axios).
// Il blob viene ricreato con un tipo scelto da noi, non da quello dichiarato dal server.
export const fetchPrivateFile = async (doc) => {
    const path = getPrivateFilePath(doc);
    if (!path) {
        throw new Error('Document has no private file');
    }
    const blob = await Axios.get(path, { responseType: 'blob' });
    const mimeType = VIEWABLE_MIME_TYPES[getFileExtension(path)] || 'application/octet-stream';
    return new Blob([blob], { type: mimeType });
};

// Sorgente per <iframe>/<img>: URL pubblico o percorso privato da scaricare con fetchPrivateFile.
export const getDocumentSource = (doc) => ({
    publicUrl: getPublicUrl(doc),
    isPrivate: Boolean(getPrivateFilePath(doc)),
});

// Apre il documento in un nuovo tab (se il browser lo sa rendere) o lo scarica.
// Va chiamata dentro un gesto dell'utente (click).
export const openDocument = async (doc, { download = false } = {}) => {
    const publicUrl = getPublicUrl(doc);
    if (publicUrl) {
        window.open(publicUrl, '_blank', 'noopener,noreferrer');
        return;
    }

    const path = getPrivateFilePath(doc);
    if (!path) {
        message.error('File not available');
        return;
    }

    // Il tab si apre subito: dopo l'await il browser bloccherebbe il popup. L'opener
    // si azzera a mano perche' 'noopener' farebbe ritornare null a window.open.
    const canView = !download && getFileExtension(path) in VIEWABLE_MIME_TYPES;
    const viewTab = canView ? window.open('', '_blank') : null;
    if (viewTab) {
        viewTab.opener = null;
    }

    try {
        const blob = await fetchPrivateFile(doc);
        const objectUrl = URL.createObjectURL(blob);
        if (viewTab) {
            viewTab.location.href = objectUrl;
        } else {
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = getDownloadName(doc, path);
            link.click();
        }
        setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_TTL);
    } catch (error) {
        console.error('Open private file error => ', error);
        if (viewTab) {
            viewTab.close();
        }
        message.error("Couldn't open the file");
    }
};
